import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type MutableRefObject,
} from 'react';
import { Canvas, useFrame, useThree, type ThreeEvent } from '@react-three/fiber';
import { OrbitControls } from '@react-three/drei';
import * as THREE from 'three';
import {
  COTTAGE_TOWN_MAX_STRUCTURES,
  COTTAGE_TOWN_MIN_STRUCTURES,
  getCottageTownPageSession,
  type CottageTownCanonicalEvent,
  type CottageTownPageSession,
  type CottageTownSnapshot,
} from '../core/pageSessions/cottageTownPageSession';
import type { CottageStructureAdapter } from '../core/pageSessions/cottageStructureAdapter';
import type { CottageImpactResult, CottageRuntime, CottageSnapshot, CottageWeapon } from '../structures/cottage/cottagePhysics';
import { COTTAGE_PIECES, type CottageLayerId, type CottagePieceSpec } from '../structures/cottage/cottageSpec';
import {
  DESTRUCTION_PROFILES,
  PROFILE_IDS,
  type ProfileId,
} from '../services/destructionProfiles';
import { createSurfaceMaterial } from '../materials/core/SurfaceMaterial';
import {
  assignTileableBoxUvs,
  BAKED_PROFILE_IDS,
  bakeSharedProfiles,
  isBakedProfile,
  type BakedProfileId,
} from '../materials/core/DemolitionBakedMaterials';
import './CottageTown.css';
import { ImpactFx, type DemolitionFxController } from '../effects/fallback/ImpactFx';
import {
  createDemolitionVisualState,
  resolveDemolitionEffectTier,
  type DemolitionEffectTier,
  type DemolitionVisualState,
} from '../effects/presentation/effectTier';
import { dispatchCottagePresentation } from '../effects/presentation/cottagePresentationDispatcher';
import {
  COTTAGE_TOWN_PARTICLE_ARENA,
  COTTAGE_TOWN_PARTICLE_COLLIDERS,
  DemolitionParticleLayer,
  overlayPresentationParticles,
  type DemolitionParticleController,
} from '../effects/particles';

const WEAPONS: ReadonlyArray<{
  id: CottageWeapon;
  label: string;
  mark: string;
  effect: string;
}> = [
  { id: 'cannon', label: 'Cannon', mark: '●', effect: 'Broad kinetic crater' },
  { id: 'spike', label: 'Spike', mark: '◆', effect: 'Deep shear channel' },
  { id: 'rubble', label: 'Rubble', mark: '⬢', effect: 'Wide surface crush' },
  { id: 'concussion', label: 'Concussion', mark: '◉', effect: 'Structural pulse' },
  { id: 'bomb', label: 'Charge', mark: '✦', effect: 'Multi-part blast' },
];

const MATERIAL_COPY: Record<ProfileId, string> = {
  concrete: 'lintels + chimney',
  brick: 'masonry walls',
  wood: 'frame + roof + door',
  stone: 'foundation',
  glass: 'window panes',
  plaster: 'upper infill',
};

const WEAPON_COLORS: Record<CottageWeapon, string> = {
  cannon: '#ffb36b',
  spike: '#d8efff',
  rubble: '#d8a47b',
  concussion: '#79d9ff',
  bomb: '#ff684f',
};

const CUTAWAY_LAYERS: CottageLayerId[] = [
  'foundation',
  'floor-frame',
  'exterior-brick',
  'interior-plaster',
  'opening',
  'glazing',
  'roof-frame',
  'roof-cover',
  'chimney',
];

const STRUCTURE_LABELS = ['A', 'B', 'C'] as const;
const STRIKE_PART = 'glass-pane-1-f';

type StructureCount = 1 | 2 | 3;
type CottageTownMode = 'cottage' | 'town';

interface CottageBridgeState extends CottageSnapshot {
  loaded: boolean;
  canvasReady: boolean;
  bakedActive: boolean;
  bakeFailed: boolean;
  bakeMs: Partial<Record<BakedProfileId, number>>;
  totalBakeMs: number;
  weapon: CottageWeapon;
  power: number;
  cutawayLayer: CottageLayerId | null;
  mode: CottageTownMode;
  count: StructureCount;
}

interface CottageProbe {
  impactAt(
    x: number,
    y: number,
    z: number,
    weapon?: CottageWeapon,
    power?: number,
  ): CottageImpactResult | null;
  impactPart(
    id: string,
    weapon?: CottageWeapon,
    power?: number,
  ): CottageImpactResult | null;
  reset(): void;
  snapshot(): CottageSnapshot | null;
  partIds(kind?: CottagePieceSpec['kind']): string[];
  setTool(tool: CottageWeapon): void;
  setPower(power: number): void;
  setCutawayLayer(layer: CottageLayerId | null): void;
}

interface TownBridgeState extends CottageTownSnapshot {
  loaded: boolean;
  canvasReady: boolean;
  bakedActive: boolean;
  bakeFailed: boolean;
  bakeMs: Partial<Record<BakedProfileId, number>>;
  totalBakeMs: number;
  mode: CottageTownMode;
  count: StructureCount;
}

interface CottageVisualPack {
  materials: Record<ProfileId, THREE.Material>;
  bakeMs: Record<BakedProfileId, number>;
  tileMeters: number;
  totalBakeMs: number;
}

interface CottageVisualReady {
  count: StructureCount;
  bakeMs: Record<BakedProfileId, number>;
  totalBakeMs: number;
}

interface TownProbe {
  strikePart(structure: number, pieceId?: string): void;
  reset(structure: number): void;
  dispose(structure: number): void;
  snapshot(): CottageTownSnapshot | null;
}

declare global {
  interface Window {
    __cottageState?: CottageBridgeState;
    __cottageProbe?: CottageProbe;
    __cottageTownState?: TownBridgeState;
    __cottageTownProbe?: TownProbe;
  }
}

function isStructureCount(value: number): value is StructureCount {
  return Number.isInteger(value) && value >= COTTAGE_TOWN_MIN_STRUCTURES && value <= COTTAGE_TOWN_MAX_STRUCTURES;
}

function pathDefaultCount(pathname: string): StructureCount {
  return pathname === '/town' || pathname.startsWith('/town/') ? 2 : 1;
}

function readCountFromLocation(location = window.location): StructureCount {
  const raw = new URLSearchParams(location.search).get('count');
  if (raw != null && raw !== '') {
    const parsed = Number(raw);
    if (isStructureCount(parsed)) return parsed;
  }
  return pathDefaultCount(location.pathname);
}

function writeCountToLocation(count: StructureCount): void {
  const url = new URL(window.location.href);
  if (url.searchParams.get('count') === String(count)) return;
  url.searchParams.set('count', String(count));
  const next = `${url.pathname}${url.search}${url.hash}`;
  const current = `${window.location.pathname}${window.location.search}${window.location.hash}`;
  if (current !== next) window.history.pushState({}, '', next);
}

function createCottageGlassMaterial(name: string): THREE.MeshStandardMaterial {
  const params = DESTRUCTION_PROFILES.glass.skin.outer;
  const material = createSurfaceMaterial(params, {
    roughness: params.roughness,
    metalness: params.metalness ?? 0,
    shaderOwnsAlbedo: false,
  });
  material.name = name;
  material.transparent = true;
  material.opacity = 0.48;
  material.depthWrite = false;
  material.side = THREE.DoubleSide;
  return material;
}

function buildPieceGeometry(spec: CottagePieceSpec, tileMeters: number): THREE.BoxGeometry {
  const geometry = new THREE.BoxGeometry(spec.half[0] * 2, spec.half[1] * 2, spec.half[2] * 2);
  geometry.setAttribute('surfaceRestPosition', geometry.getAttribute('position').clone());
  geometry.setAttribute('surfaceRestNormal', geometry.getAttribute('normal').clone());
  if (isBakedProfile(spec.material)) {
    assignTileableBoxUvs(geometry, tileMeters);
    try {
      geometry.computeTangents();
    } catch {
      // Box faces always have a tangent frame; keep going if a runtime lacks it.
    }
  }
  return geometry;
}

function useCottageVisualMaterials(onFailed: (message: string) => void): CottageVisualPack | null {
  const { gl } = useThree();
  const [pack, setPack] = useState<CottageVisualPack | null>(null);
  const failedRef = useRef(onFailed);
  failedRef.current = onFailed;
  const disposeRef = useRef<(() => void) | null>(null);

  useEffect(() => {
    let cancelled = false;
    requestAnimationFrame(() =>
      requestAnimationFrame(() => {
        if (cancelled) return;
        try {
          const baked = bakeSharedProfiles(gl);
          const glass = createCottageGlassMaterial('cottage-glass');
          for (const id of BAKED_PROFILE_IDS) {
            baked.materials[id].name = `cottage-${id}`;
          }
          if (cancelled) {
            baked.dispose();
            glass.dispose();
            return;
          }
          disposeRef.current?.();
          disposeRef.current = () => {
            baked.dispose();
            glass.dispose();
          };
          setPack({
            materials: { ...baked.materials, glass },
            bakeMs: baked.bakeMs,
            tileMeters: baked.tileMeters,
            totalBakeMs: baked.totalBakeMs,
          });
        } catch (reason: unknown) {
          disposeRef.current?.();
          disposeRef.current = null;
          setPack(null);
          failedRef.current(reason instanceof Error ? reason.message : String(reason));
        }
      }),
    );
    return () => {
      cancelled = true;
      disposeRef.current?.();
      disposeRef.current = null;
    };
  }, [gl]);

  return pack;
}

function CottageBakeGate({
  onFailed,
  children,
}: {
  onFailed: (message: string) => void;
  children: (pack: CottageVisualPack) => React.ReactNode;
}) {
  const pack = useCottageVisualMaterials(onFailed);
  if (!pack) return null;
  return <>{children(pack)}</>;
}

function StructureCountControl({
  count,
  onChange,
}: {
  count: StructureCount;
  onChange: (count: StructureCount) => void;
}) {
  return (
    <div className="cottage-count-control" role="group" aria-label="Structure count" data-testid="cottage-town-count">
      <span>Structures</span>
      <input
        type="number"
        min={COTTAGE_TOWN_MIN_STRUCTURES}
        max={COTTAGE_TOWN_MAX_STRUCTURES}
        step={1}
        value={count}
        aria-label="Structure count"
        data-testid="cottage-town-count-input"
        onChange={(event) => {
          const next = Number(event.currentTarget.value);
          if (isStructureCount(next)) onChange(next);
        }}
      />
      <div className="cottage-count-buttons">
        {([1, 2, 3] as const).map((value) => (
          <button
            key={value}
            type="button"
            data-testid={`cottage-town-count-${value}`}
            aria-pressed={count === value}
            onClick={() => onChange(value)}
          >
            {value}
          </button>
        ))}
      </div>
    </div>
  );
}

function CottagePiece({
  spec,
  index,
  material,
  tileMeters,
  registerMesh,
  onImpact,
  visible = true,
}: {
  spec: CottagePieceSpec;
  index: number;
  material: THREE.Material;
  tileMeters: number;
  registerMesh: (index: number, mesh: THREE.Mesh | null) => void;
  onImpact: (point: THREE.Vector3) => void;
  visible?: boolean;
}) {
  const geometry = useMemo(() => buildPieceGeometry(spec, tileMeters), [spec, tileMeters]);
  useEffect(() => () => geometry.dispose(), [geometry]);

  const assignRef = useCallback(
    (mesh: THREE.Mesh | null) => {
      registerMesh(index, mesh);
    },
    [index, registerMesh],
  );

  const handleClick = useCallback(
    (event: ThreeEvent<MouseEvent>) => {
      event.stopPropagation();
      onImpact(event.point.clone());
    },
    [onImpact],
  );

  return (
    <mesh
      ref={assignRef}
      geometry={geometry}
      material={material}
      visible={visible}
      position={spec.position}
      quaternion={spec.rotation}
      castShadow={spec.material !== 'glass'}
      receiveShadow
      renderOrder={spec.material === 'glass' ? 2 : 0}
      onClick={handleClick}
      name={spec.id}
      userData={{ cottagePartId: spec.id, kind: spec.kind, material: spec.material }}
    />
  );
}

function ImpactPulse({
  point,
  weapon,
  sequence,
  onDone,
}: {
  point: THREE.Vector3;
  weapon: CottageWeapon;
  sequence: number;
  onDone: (sequence: number) => void;
}) {
  const meshRef = useRef<THREE.Mesh>(null);
  const materialRef = useRef<THREE.MeshBasicMaterial>(null);
  const bornAt = useRef<number | null>(null);

  useFrame(({ clock }) => {
    if (bornAt.current == null) bornAt.current = clock.elapsedTime;
    const age = clock.elapsedTime - bornAt.current;
    const t = Math.min(1, age / 0.48);
    if (meshRef.current) meshRef.current.scale.setScalar(0.28 + t * 2.1);
    if (materialRef.current) materialRef.current.opacity = (1 - t) * 0.82;
    if (t >= 1) onDone(sequence);
  });

  return (
    <mesh ref={meshRef} position={point}>
      <sphereGeometry args={[0.12, 14, 10]} />
      <meshBasicMaterial
        ref={materialRef}
        color={WEAPON_COLORS[weapon]}
        transparent
        opacity={0.82}
        depthWrite={false}
        wireframe
      />
    </mesh>
  );
}

function CottageGround() {
  return (
    <>
      <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, -0.025, 0]} receiveShadow>
        <circleGeometry args={[18, 96]} />
        <meshStandardMaterial color="#4e5d44" roughness={0.98} />
      </mesh>
      <gridHelper
        args={[36, 36, '#728064', '#5d6953']}
        position={[0, 0.006, 0]}
        material-transparent
        material-opacity={0.22}
      />
    </>
  );
}

function dispatchCottageTownPresentation(
  session: CottageTownPageSession,
  fx: DemolitionFxController | null,
  particles: DemolitionParticleController | null,
  effectsEnabled: boolean,
): void {
  const events = session.drainCanonicalEvents();
  if (events.length === 0) return;
  const effects = overlayPresentationParticles(fx, particles);
  const groups = new Map<string, CottageTownCanonicalEvent[]>();
  for (const event of events) {
    const batch = groups.get(event.structureKey);
    if (batch) batch.push(event);
    else groups.set(event.structureKey, [event]);
  }
  for (const [structureKey, batch] of groups) {
    const adapter = session.adapters.find((candidate) => candidate.structureKey === structureKey);
    dispatchCottagePresentation({
      structureKey,
      worldOffset: adapter?.worldOffset ?? { x: 0, y: 0, z: 0 },
      events: batch,
      effects,
      effectsEnabled,
    });
  }
}

function InspectionScene({
  session,
  runtime,
  weapon,
  power,
  materials,
  tileMeters,
  bakeMs,
  totalBakeMs,
  onSnapshot,
  onCanvasReady,
  cutawayLayer,
  fxRef,
  particleRef,
  visualState,
}: {
  session: CottageTownPageSession;
  runtime: CottageRuntime;
  weapon: CottageWeapon;
  power: number;
  materials: Record<ProfileId, THREE.Material>;
  tileMeters: number;
  bakeMs: Record<BakedProfileId, number>;
  totalBakeMs: number;
  onSnapshot: (snapshot: CottageTownSnapshot) => void;
  onCanvasReady: (info: CottageVisualReady) => void;
  cutawayLayer: CottageLayerId | null;
  fxRef: MutableRefObject<DemolitionFxController | null>;
  particleRef: MutableRefObject<DemolitionParticleController | null>;
  visualState: DemolitionVisualState;
}) {
  const { camera } = useThree();
  const meshRefs = useRef<Array<THREE.Mesh | null>>([]);
  const lastPublish = useRef(0);
  const readyPublished = useRef(false);
  const impactSeq = useRef(0);
  const [pulse, setPulse] = useState<{
    point: THREE.Vector3;
    weapon: CottageWeapon;
    sequence: number;
  } | null>(null);

  const registerMesh = useCallback((index: number, mesh: THREE.Mesh | null) => {
    meshRefs.current[index] = mesh;
  }, []);

  const strike = useCallback(
    (point: THREE.Vector3) => {
      const direction = point.clone().sub(camera.position).normalize();
      session.impactAt({ x: point.x, y: point.y, z: point.z }, weapon, power, { x: direction.x, y: direction.y, z: direction.z });
      impactSeq.current += 1;
      setPulse({ point, weapon, sequence: impactSeq.current });
      onSnapshot(session.snapshot());
    },
    [camera, onSnapshot, power, session, weapon],
  );

  useFrame(({ clock }, delta) => {
    session.advance(Math.min(delta, 0.05));
    // The scene owns the compatibility queue; consume it once even though
    // Cottage presentation is driven by the richer canonical event stream.
    session.drainEvents();
    dispatchCottageTownPresentation(
      session,
      fxRef.current,
      particleRef.current,
      visualState.mounted,
    );
    const transforms = runtime.transforms;
    for (let i = 0; i < runtime.pieces.length; i += 1) {
      const mesh = meshRefs.current[i];
      if (!mesh) continue;
      const offset = i * 7;
      mesh.position.set(transforms[offset], transforms[offset + 1], transforms[offset + 2]);
      mesh.quaternion.set(
        transforms[offset + 3],
        transforms[offset + 4],
        transforms[offset + 5],
        transforms[offset + 6],
      );
    }
    if (!readyPublished.current) {
      readyPublished.current = true;
      onCanvasReady({ count: session.requestedStructureCount as StructureCount, bakeMs, totalBakeMs });
      onSnapshot(session.snapshot());
    }
    if (clock.elapsedTime - lastPublish.current >= 0.16) {
      lastPublish.current = clock.elapsedTime;
      onSnapshot(session.snapshot());
    }
  });

  return (
    <>
      <color attach="background" args={['#92a4ad']} />
      <fog attach="fog" args={['#92a4ad', 18, 42]} />
      <hemisphereLight args={['#e9f4ff', '#394332', 1.35]} />
      <directionalLight
        castShadow
        position={[8, 13, 10]}
        intensity={2.8}
        color="#fff0d5"
        shadow-mapSize-width={2048}
        shadow-mapSize-height={2048}
        shadow-camera-left={-10}
        shadow-camera-right={10}
        shadow-camera-top={10}
        shadow-camera-bottom={-10}
        shadow-camera-near={1}
        shadow-camera-far={35}
        shadow-bias={-0.00025}
      />
      <CottageGround />
      {runtime.pieces.map((piece, index) => (
        <CottagePiece
          key={piece.spec.id}
          spec={piece.spec}
          index={index}
          material={materials[piece.spec.material]}
          tileMeters={tileMeters}
          registerMesh={registerMesh}
          onImpact={strike}
          visible={piece.spec.visible && (cutawayLayer === null || piece.spec.layer === cutawayLayer)}
        />
      ))}
      {pulse && (
        <ImpactPulse
          point={pulse.point}
          weapon={pulse.weapon}
          sequence={pulse.sequence}
          onDone={(sequence) => {
            setPulse((current) => (current?.sequence === sequence ? null : current));
          }}
        />
      )}
      <OrbitControls
        makeDefault
        target={[0, 2.35, 0]}
        minDistance={7}
        maxDistance={24}
        minPolarAngle={0.35}
        maxPolarAngle={Math.PI * 0.49}
        enableDamping
        dampingFactor={0.075}
      />
    </>
  );
}

function TownPiece({
  adapter,
  index,
  material,
  tileMeters,
  registerMesh,
  onStrike,
}: {
  adapter: CottageStructureAdapter;
  index: number;
  material: THREE.Material;
  tileMeters: number;
  registerMesh: (key: string, mesh: THREE.Mesh | null) => void;
  onStrike: (adapter: CottageStructureAdapter, pieceId: string) => void;
}) {
  const spec = COTTAGE_PIECES[index];
  const geometry = useMemo(() => buildPieceGeometry(spec, tileMeters), [spec, tileMeters]);
  const key = `${adapter.structureKey}:${spec.id}`;
  useEffect(() => () => geometry.dispose(), [geometry]);
  const assignRef = useCallback((mesh: THREE.Mesh | null) => registerMesh(key, mesh), [key, registerMesh]);
  const handleClick = useCallback((event: ThreeEvent<MouseEvent>) => {
    event.stopPropagation();
    onStrike(adapter, spec.id);
  }, [adapter, onStrike, spec.id]);
  if (!spec.visible) return null;
  return (
    <mesh
      ref={assignRef}
      geometry={geometry}
      material={material}
      position={spec.position}
      quaternion={spec.rotation}
      castShadow={spec.material !== 'glass'}
      receiveShadow
      name={key}
      userData={{ structureKey: adapter.structureKey, cottagePartId: spec.id }}
      onClick={handleClick}
    />
  );
}

function TownScene({
  session,
  materials,
  tileMeters,
  bakeMs,
  totalBakeMs,
  onSnapshot,
  onCanvasReady,
  fxRef,
  particleRef,
  visualState,
}: {
  session: CottageTownPageSession;
  materials: Record<ProfileId, THREE.Material>;
  tileMeters: number;
  bakeMs: Record<BakedProfileId, number>;
  totalBakeMs: number;
  onSnapshot: (snapshot: CottageTownSnapshot) => void;
  onCanvasReady: (info: CottageVisualReady) => void;
  fxRef: MutableRefObject<DemolitionFxController | null>;
  particleRef: MutableRefObject<DemolitionParticleController | null>;
  visualState: DemolitionVisualState;
}) {
  const meshRefs = useRef(new Map<string, THREE.Mesh>());
  const ready = useRef(false);
  const publishElapsed = useRef(0);
  const registerMesh = useCallback((key: string, mesh: THREE.Mesh | null) => {
    if (mesh) meshRefs.current.set(key, mesh);
    else meshRefs.current.delete(key);
  }, []);
  const strike = useCallback((adapter: CottageStructureAdapter, pieceId: string) => {
    session.strikePart(adapter.structureKey, pieceId, 'spike', 1.5);
  }, [session]);

  useFrame((_, delta) => {
    session.advance(Math.min(delta, 0.05));
    session.drainEvents();
    dispatchCottageTownPresentation(
      session,
      fxRef.current,
      particleRef.current,
      visualState.mounted,
    );
    for (const adapter of session.adapters) {
      if (adapter.isDisposed) continue;
      const transforms = adapter.cottageRuntime.transforms;
      for (let index = 0; index < adapter.cottageRuntime.pieces.length; index += 1) {
        const piece = adapter.cottageRuntime.pieces[index];
        const mesh = meshRefs.current.get(`${adapter.structureKey}:${piece.spec.id}`);
        if (!mesh || !piece.spec.visible) continue;
        const offset = index * 7;
        mesh.position.set(transforms[offset], transforms[offset + 1], transforms[offset + 2]);
        mesh.quaternion.set(transforms[offset + 3], transforms[offset + 4], transforms[offset + 5], transforms[offset + 6]);
      }
    }
    publishElapsed.current += delta;
    if (!ready.current) {
      ready.current = true;
      onCanvasReady({
        count: session.requestedStructureCount as StructureCount,
        bakeMs,
        totalBakeMs,
      });
      onSnapshot(session.snapshot());
    } else if (publishElapsed.current >= 0.16) {
      publishElapsed.current = 0;
      onSnapshot(session.snapshot());
    }
  });

  const planeSpan = session.requestedStructureCount >= 3 ? 64 : 48;

  return (
    <>
      <color attach="background" args={['#71838a']} />
      <fog attach="fog" args={['#71838a', 34, 90]} />
      <hemisphereLight args={['#e9f4ff', '#394332', 1.35]} />
      <directionalLight castShadow position={[8, 16, 12]} intensity={2.5} color="#fff0d5" shadow-mapSize-width={2048} shadow-mapSize-height={2048} shadow-camera-left={-18} shadow-camera-right={18} shadow-camera-top={16} shadow-camera-bottom={-16} shadow-camera-far={60} />
      <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, -0.025, 0]} receiveShadow>
        <planeGeometry args={[planeSpan, 32]} />
        <meshStandardMaterial color="#4e5d44" roughness={0.98} />
      </mesh>
      {session.adapters.filter((adapter) => !adapter.isDisposed).map((adapter) => (
        <group key={adapter.structureKey}>
          {COTTAGE_PIECES.map((_, index) => (
            <TownPiece key={`${adapter.structureKey}:${COTTAGE_PIECES[index].id}`} adapter={adapter} index={index} material={materials[COTTAGE_PIECES[index].material]} tileMeters={tileMeters} registerMesh={registerMesh} onStrike={strike} />
          ))}
        </group>
      ))}
      <OrbitControls makeDefault target={[0, 2.4, 0]} minDistance={11} maxDistance={80} minPolarAngle={0.35} maxPolarAngle={Math.PI * 0.49} enableDamping dampingFactor={0.075} />
    </>
  );
}

function townCameraForCount(count: StructureCount) {
  const narrow = window.innerWidth <= 650;
  if (count <= 2) {
    return {
      position: (narrow ? [30, 14, -48] : [20, 10, -24]) as [number, number, number],
      fov: narrow ? 55 : 43,
      near: 0.1,
      far: 100,
    };
  }
  return {
    position: (narrow ? [40, 18, -56] : [28, 14, -36]) as [number, number, number],
    fov: narrow ? 55 : 43,
    near: 0.1,
    far: 120,
  };
}

export default function CottageTown({ onBack }: { onBack: () => void }) {
  const [count, setCount] = useState<StructureCount>(() => readCountFromLocation());
  const [session, setSession] = useState<CottageTownPageSession | null>(null);
  const [snapshot, setSnapshot] = useState<CottageTownSnapshot | null>(null);
  const [weapon, setWeapon] = useState<CottageWeapon>('cannon');
  const [power, setPower] = useState(1);
  const [cutawayLayer, setCutawayLayer] = useState<CottageLayerId | null>(null);
  const [visualReady, setVisualReady] = useState<CottageVisualReady | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [bakeError, setBakeError] = useState<string | null>(null);
  const fxRef = useRef<DemolitionFxController | null>(null);
  const particleRef = useRef<DemolitionParticleController | null>(null);
  const effectTier = useMemo((): DemolitionEffectTier => resolveDemolitionEffectTier(), []);
  const visualState = useMemo(() => createDemolitionVisualState(effectTier), [effectTier]);

  const inspection = count === 1;
  const mode: CottageTownMode = inspection ? 'cottage' : 'town';
  const sessionReady = session != null && !session.isDisposed && session.requestedStructureCount === count;
  const canvasReady = visualReady?.count === count;
  const displayError = error ?? bakeError;
  const cottage = snapshot?.structures[0]?.cottage ?? null;
  const townCamera = useMemo(() => townCameraForCount(count), [count]);

  const applyCount = useCallback((next: StructureCount, writeUrl: boolean) => {
    setCount((current) => (current === next ? current : next));
    if (writeUrl) writeCountToLocation(next);
  }, []);

  useEffect(() => {
    const onPop = () => applyCount(readCountFromLocation(), false);
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, [applyCount]);

  const handleBakeFailed = useCallback((message: string) => {
    setBakeError(message);
  }, []);

  const handleCanvasReady = useCallback((info: CottageVisualReady) => {
    setVisualReady(info);
  }, []);

  useEffect(() => {
    let cancelled = false;
    setError(null);
    setBakeError(null);
    getCottageTownPageSession(count)
      .then((next) => {
        if (cancelled) return;
        setSession(next);
        setSnapshot(next.snapshot());
      })
      .catch((reason: unknown) => {
        if (!cancelled) setError(reason instanceof Error ? reason.message : String(reason));
      });
    return () => {
      cancelled = true;
    };
  }, [count]);

  const publishSnapshot = useCallback((next: CottageTownSnapshot) => {
    setSnapshot(next);
  }, []);

  const handleReset = useCallback(() => {
    if (!sessionReady || !session) return;
    session.reset();
    setSnapshot(session.snapshot());
  }, [session, sessionReady]);

  useEffect(() => {
    const bakeReady = canvasReady && bakeError == null && visualReady != null;
    if (!sessionReady || !session || !snapshot || !bakeReady) {
      delete window.__cottageState;
      delete window.__cottageProbe;
      delete window.__cottageTownState;
      delete window.__cottageTownProbe;
      return;
    }

    const primary = snapshot.structures[0]?.cottage ?? null;
    const bakeMs = visualReady.bakeMs;
    const totalBakeMs = visualReady.totalBakeMs;
    if (primary) {
      window.__cottageState = {
        ...primary,
        loaded: bakeReady,
        canvasReady,
        bakedActive: bakeReady,
        bakeFailed: bakeError != null,
        bakeMs,
        totalBakeMs,
        schemaVersion: primary.schemaVersion,
        weapon,
        power,
        cutawayLayer,
        mode,
        count,
      };
    }
    window.__cottageProbe = {
      impactAt(x, y, z, selectedWeapon = weapon, selectedPower = power) {
        session.impactAt({ x, y, z }, selectedWeapon, selectedPower, { x: 0, y: 0, z: 1 });
        return null;
      },
      impactPart(id, selectedWeapon = weapon, selectedPower = power) {
        if (!session.runtime.pieces.some((piece) => piece.spec.id === id)) return null;
        session.impactPart(id, selectedWeapon, selectedPower);
        return null;
      },
      reset() {
        session.reset();
        setSnapshot(session.snapshot());
      },
      snapshot() {
        return session.snapshot().structures[0]?.cottage ?? null;
      },
      partIds(kind) {
        return session.runtime.pieces
          .filter((piece) => kind == null || piece.spec.kind === kind)
          .map((piece) => piece.spec.id);
      },
      setTool(tool) { setWeapon(tool); session.setTool(tool); },
      setPower(nextPower) { setPower(nextPower); session.setPower(nextPower); },
      setCutawayLayer(layer) { setCutawayLayer(layer); },
    };
    window.__cottageTownState = {
      ...snapshot,
      loaded: bakeReady,
      canvasReady,
      bakedActive: bakeReady,
      bakeFailed: bakeError != null,
      bakeMs,
      totalBakeMs,
      mode,
      count,
    };
    window.__cottageTownProbe = {
      strikePart(structure, pieceId = STRIKE_PART) {
        const adapter = session.adapters[structure];
        if (adapter && !adapter.isDisposed) session.strikePart(adapter.structureKey, pieceId, 'spike', 1.5);
      },
      reset(structure) {
        const adapter = session.adapters[structure];
        if (adapter && !adapter.isDisposed) session.resetStructure(adapter.structureKey);
      },
      dispose(structure) {
        const adapter = session.adapters[structure];
        if (adapter && !adapter.isDisposed) session.disposeStructure(adapter.structureKey);
      },
      snapshot() { return session.snapshot(); },
    };
  }, [bakeError, canvasReady, count, cutawayLayer, mode, power, session, sessionReady, snapshot, visualReady, weapon]);

  useEffect(() => () => {
    delete window.__cottageState;
    delete window.__cottageProbe;
    delete window.__cottageTownState;
    delete window.__cottageTownProbe;
  }, []);

  const residueGeneration = snapshot?.structures.reduce(
    (sum, structure) => sum + structure.cottage.canonicalEpoch,
    0,
  ) ?? 0;
  const selectedWeapon = WEAPONS.find((candidate) => candidate.id === weapon) ?? WEAPONS[0];
  const releasedPercent = cottage && cottage.totalPieces > 0
    ? Math.round((cottage.releasedPieces / cottage.totalPieces) * 100)
    : 0;
  const activeStructures = snapshot?.structures ?? [];
  const loading = (!sessionReady || !canvasReady) && !displayError;

  return (
    <main className={inspection ? 'cottage-page' : 'cottage-town-page'} data-testid="cottage-town-page">
      <div className={inspection ? 'cottage-stage' : 'cottage-town-stage'} data-testid={inspection ? 'cottage-stage' : 'cottage-town-stage'}>
        <Canvas
          key={`cottage-town-${count}`}
          shadows="basic"
          camera={inspection
            ? { position: [11.5, 7.2, -12.5], fov: 43, near: 0.1, far: 80 }
            : townCamera}
          gl={{ antialias: true, powerPreference: 'high-performance' }}
          onCreated={({ gl }) => {
            gl.toneMapping = THREE.ACESFilmicToneMapping;
            gl.toneMappingExposure = 1.08;
            gl.outputColorSpace = THREE.SRGBColorSpace;
            gl.domElement.style.touchAction = 'none';
          }}
        >
          {sessionReady && session && (
            <CottageBakeGate onFailed={handleBakeFailed}>
              {(pack) =>
                inspection ? (
                  <InspectionScene
                    session={session}
                    runtime={session.runtime}
                    weapon={weapon}
                    power={power}
                    materials={pack.materials}
                    tileMeters={pack.tileMeters}
                    bakeMs={pack.bakeMs}
                    totalBakeMs={pack.totalBakeMs}
                    cutawayLayer={cutawayLayer}
                    onSnapshot={publishSnapshot}
                    onCanvasReady={handleCanvasReady}
                    fxRef={fxRef}
                    particleRef={particleRef}
                    visualState={visualState}
                  />
                ) : (
                  <TownScene
                    session={session}
                    materials={pack.materials}
                    tileMeters={pack.tileMeters}
                    bakeMs={pack.bakeMs}
                    totalBakeMs={pack.totalBakeMs}
                    onSnapshot={publishSnapshot}
                    onCanvasReady={handleCanvasReady}
                    fxRef={fxRef}
                    particleRef={particleRef}
                    visualState={visualState}
                  />
                )
              }
            </CottageBakeGate>
          )}
          {visualState.mounted && (
            <ImpactFx
              key={`cottage-fx-${residueGeneration}`}
              fxRef={fxRef}
              visualState={visualState}
            />
          )}
          {visualState.mounted && (
            <DemolitionParticleLayer
              effectTier={effectTier}
              residueGeneration={residueGeneration}
              controllerRef={particleRef}
              arenaBounds={COTTAGE_TOWN_PARTICLE_ARENA}
              colliders={COTTAGE_TOWN_PARTICLE_COLLIDERS}
            />
          )}
        </Canvas>
      </div>

      {inspection ? (
        <>
          <header className="cottage-topbar">
            <div className="cottage-brand">
              <button type="button" className="cottage-back" onClick={onBack} data-testid="cottage-back">
                ← Labs
              </button>
              <div>
                <p className="cottage-kicker">STRUCTURAL DEMOLITION</p>
                <h1>Material Cottage</h1>
              </div>
              <StructureCountControl count={count} onChange={(next) => applyCount(next, true)} />
            </div>
            <div className="cottage-stats" data-testid="cottage-stats">
              <div><span>Intact</span><strong>{cottage?.intactPieces ?? 0}</strong></div>
              <div><span>Released</span><strong>{cottage?.releasedPieces ?? 0}</strong></div>
              <div><span>Awake</span><strong>{cottage?.awakePieces ?? 0}</strong></div>
              <div className="cottage-damage-stat"><span>Structure</span><strong>{releasedPercent}% damaged</strong></div>
            </div>
          </header>

          <aside className="cottage-brief" data-testid="cottage-brief">
            <span className="cottage-brief-rule" />
            <p><strong>Click any part to strike it.</strong> Stone piers carry the timber frame; the frame carries the wall skins and roof.</p>
            <p className="cottage-selected-effect">{selectedWeapon.effect} · {power.toFixed(2)}× power</p>
            {(cottage?.cascadeReleased ?? 0) > 0 && (
              <p className="cottage-cascade" role="status">
                Support cascade: {cottage?.cascadeReleased} dependent parts released
              </p>
            )}
          </aside>

          <aside className="cottage-material-legend" aria-label="Cottage materials">
            <p className="cottage-panel-label">ASSEMBLY</p>
            {PROFILE_IDS.map((id) => (
              <div key={id} className={`cottage-material-row is-${id}`}>
                <span className="cottage-material-swatch" />
                <strong>{DESTRUCTION_PROFILES[id].label}</strong>
                <small>{MATERIAL_COPY[id]}</small>
              </div>
            ))}
          </aside>

          <section className="cottage-weapon-dock" aria-label="Demolition controls">
            <div className="cottage-weapons" role="group" aria-label="Weapon">
              {WEAPONS.map((option) => (
                <button
                  key={option.id}
                  type="button"
                  className={`cottage-weapon${weapon === option.id ? ' is-active' : ''}`}
                  data-testid={`cottage-weapon-${option.id}`}
                  aria-pressed={weapon === option.id}
                  title={option.effect}
                  onClick={() => setWeapon(option.id)}
                >
                  <span aria-hidden="true">{option.mark}</span>
                  <small>{option.label}</small>
                </button>
              ))}
            </div>
            <label className="cottage-power">
              <span>Power <strong>{power.toFixed(2)}×</strong></span>
              <input
                type="range"
                min="0.75"
                max="1.75"
                step="0.05"
                value={power}
                aria-label="Weapon power"
                data-testid="cottage-power"
                onChange={(event) => setPower(Number(event.currentTarget.value))}
              />
            </label>
            <button
              type="button"
              className="cottage-reset"
              onClick={handleReset}
              disabled={!sessionReady}
              data-testid="cottage-reset"
            >
              Rebuild cottage
            </button>
          </section>

          <section className="cottage-cutaway" aria-label="Layer isolation controls">
            <p className="cottage-panel-label">LAYER INSPECT</p>
            <button type="button" data-testid="cottage-cutaway-all" aria-pressed={cutawayLayer === null} onClick={() => setCutawayLayer(null)}>All layers</button>
            {CUTAWAY_LAYERS.map((layer) => (
              <button key={layer} type="button" data-testid={`cottage-cutaway-${layer}`} aria-pressed={cutawayLayer === layer} onClick={() => setCutawayLayer(layer)}>{layer.replace('-', ' ')}</button>
            ))}
          </section>
        </>
      ) : (
        <>
          <header className="cottage-town-topbar">
            <div className="cottage-town-brand">
              <button type="button" className="cottage-town-back" onClick={onBack} data-testid="cottage-town-back">← Labs</button>
              <div><p>SHARED DESTRUCTION WORLD</p><h1>Cottage Town</h1></div>
              <StructureCountControl count={count} onChange={(next) => applyCount(next, true)} />
            </div>
            <div className="cottage-town-stats" data-testid="cottage-town-stats">
              <div><span>Structures</span><strong>{snapshot?.structureCount ?? 0}</strong></div>
              <div><span>Native bodies</span><strong>{snapshot?.nativeBodyCount ?? 0}</strong></div>
              <div><span>Native shapes</span><strong>{snapshot?.nativeShapeCount ?? 0}</strong></div>
            </div>
          </header>
          <aside className="cottage-town-brief">
            <strong>{count} authored cottages, one persistent physics scene.</strong>
            <span>Click a piece or use a building control. Cross-building projectile contacts are intentionally deferred.</span>
          </aside>
          <section className="cottage-town-telemetry" data-testid="cottage-town-telemetry" aria-label="Town telemetry">
            <div className="town-telemetry-heading"><span>LIVE TELEMETRY</span><strong>{snapshot?.authoredNodeCount ?? 0} authored · {snapshot?.physicalNodeCount ?? 0} physical · {snapshot?.logicalNodeCount ?? 0} logical</strong></div>
            <div className="town-telemetry-sub"><span>Registry {snapshot?.registry.structureBodyCount ?? 0} structure bodies + {snapshot?.registry.worldActorCount ?? 0} ground actor</span><span>Events retained {snapshot?.scene.events.retained ?? 0} · dropped {snapshot?.scene.events.dropped ?? 0}</span></div>
            <div className="town-telemetry-sub"><span>Unsupported cross-structure contacts {snapshot?.scene.unsupportedCrossStructureContacts ?? 0}</span><span>Scene faulted {snapshot?.scene.faulted ? 'yes' : 'no'}</span></div>
          </section>
          <section className="cottage-town-controls" aria-label="Cottage controls">
            {activeStructures.map((structure) => (
              <article key={structure.structureKey} className="cottage-town-card" data-testid={`cottage-town-card-${structure.ordinal}`}>
                <div><span>COTTAGE {STRUCTURE_LABELS[structure.ordinal] ?? structure.ordinal + 1}</span><strong>{structure.cottage.releasedPieces} released · rev {structure.revision}</strong></div>
                <small>{structure.cottage.physicalNodeCount} physical nodes · offset {structure.worldOffset.x > 0 ? '+' : ''}{structure.worldOffset.x}m X</small>
                <div className="cottage-town-card-actions">
                  <button type="button" data-testid={`cottage-town-strike-${structure.ordinal}`} onClick={() => session?.strikePart(structure.structureKey, STRIKE_PART, 'spike', 1.5)}>Strike glass</button>
                  <button type="button" data-testid={`cottage-town-reset-${structure.ordinal}`} onClick={() => session?.resetStructure(structure.structureKey)}>Reset</button>
                  <button type="button" data-testid={`cottage-town-dispose-${structure.ordinal}`} onClick={() => session?.disposeStructure(structure.structureKey)}>Dispose</button>
                </div>
              </article>
            ))}
          </section>
        </>
      )}

      {loading && inspection && (
        <div className="cottage-loading" data-testid="cottage-loading">
          <span className="cottage-spinner" />
          <strong>{sessionReady ? 'Baking cottage surfaces…' : 'Assembling cottage physics…'}</strong>
        </div>
      )}
      {loading && !inspection && (
        <div className="cottage-town-loading" data-testid="cottage-town-loading">
          <span />
          {sessionReady ? 'Baking town surfaces…' : 'Assembling shared town physics…'}
        </div>
      )}
      {displayError && inspection && (
        <div
          className="cottage-loading is-error"
          role="alert"
          data-testid={bakeError ? 'cottage-bake-error' : undefined}
        >
          <strong>{bakeError ? 'Surface bake failed' : 'Could not assemble cottage'}</strong>
          <span>{displayError}</span>
        </div>
      )}
      {displayError && !inspection && (
        <div
          className="cottage-town-loading"
          role="alert"
          data-testid={bakeError ? 'cottage-town-bake-error' : undefined}
        >
          <strong>{bakeError ? 'Surface bake failed' : 'Could not assemble town'}</strong>
          <span>{displayError}</span>
        </div>
      )}
    </main>
  );
}
